// web-v2/src/components/v3/words.js
//
// THE WORDS THE V3 ACCOUNT PAGE PUTS BESIDE A VALUE. Every state, severity,
// role and standing the payload (docs/v3-account-api.md) can carry renders as
// a word; a colour never carries the meaning alone, and an absent value is
// the NOT_RECORDED word, never an empty cell. Nothing here derives a state:
// each map turns a word the service said into the word the broker reads.
// An unmapped value renders as itself, so a new server word is visible, not
// blank.

export const SEVERITY_WORDS = Object.freeze({ blocker: 'Blocks', warning: 'Needs attention', info: 'Note' });
export const SEVERITY_CLASS = Object.freeze({
  blocker: 'bg-danger-soft text-danger',
  warning: 'bg-warning-soft text-warning',
  info: 'bg-info-soft text-info',
});
export const UNKNOWN_WORD = 'we do not know';
export const NOT_RECORDED = 'not recorded';
export const NOTHING_WAITING = 'Nothing waiting on this account';
// AIRgus that did not answer is not AIRgus that found nothing (V3 Overnight 3,
// lane C): null is a read with no AIRgus on it, { ok: false } a derivation that
// threw. Neither may render as NOTHING_WAITING.
export const AIRGUS_NOT_RUN = 'AIRgus did not run on this read, so this page cannot say whether anything is waiting';
export const AIRGUS_FAILED = 'AIRgus could not run on this account, so this page cannot say whether anything is waiting';
// The always-open PHF edit on the people card (PhfCorrection.jsx). The action says Edit;
// the audit trail still records it as a broker correction (record_corrections, the
// "broker corrected" note on the form sheet), because that is what it is.
// A pending term: a deal that exists only as a session so far (services/v3/pending-terms.js).
export const PENDING_KIND_WORDS = Object.freeze({ renewal: 'Renewal started', application: 'Application started' });
export const SESSION_STATUS_WORDS = Object.freeze({ created: 'Created', invited: 'Invited', in_progress: 'In progress', submitted: 'Submitted', presented: 'Presented', binding: 'Binding' });
export const PENDING_PILOT_WORDS = Object.freeze({ attested: 'attested', invited: 'invited, not started', in_progress: 'in progress', not_attested: 'not attested' });
export const PENDING_NOTE = 'Read from the session. This deal has no term in TRACON yet, so nothing on this block can be edited; the quote entry is in the classic deal.';
export const NO_PILOT_FORMS = 'no pilot forms on this session';
export const PHF_CORRECT_WORDS = Object.freeze({
  open: 'Edit',
  no_form: 'no PHF on file to edit',
  both_required: 'Both the new value and the reason are required',
  reason_placeholder: 'Why? Required, e.g. logbook shows 512',
  saved: "edited; the pilot's own answer stays on the form",
  unchanged: 'That is already the value on file',
  mirror_failed: 'Recorded in TRACON, but the CRM contact was not updated:',
  writes: 'Edits the form shown on this card:',
  target_unknown: 'form not named',
});
// The person edit on the people card (people-create-door C5): name, email and
// phone, through the person edit door. Broker copy (REPORT section 7).
export const PERSON_EDIT_WORDS = Object.freeze({
  open: 'Edit',
  no_contact: 'no CRM contact to edit yet',
  saved: 'saved',
  failed: 'Could not save the edit',
  registry: 'This name is confirmed against the FAA airmen registry. Change it on the person sheet, with a reason.',
});
// What a remedy says when no door on this page or on the term workspace
// will open for it: the label stays, the claim does not become a link.
export const NO_DOOR_HERE = 'no door on this page yet';

export const ROLE_WORDS = Object.freeze({
  named_insured: 'Named insured', primary_contact: 'Primary contact', pilot: 'Pilot', signer: 'Signer', payer: 'Payer', owner: 'Owner',
  member: 'Member', officer: 'Officer', cfi: 'CFI', contact: 'Contact',
});
// THE LINK DOORS (account-people-door T27 over T26's three V3 doors). The words
// equal the server's (account-link-read ROLE_WORDS, MAIN_ACCOUNT_WORD) and the
// Add roles equal the door's ADD_ROLES (primary contact has its own door);
// guard: tests/account-people-v2-surfaces.test.js W1.
export const MAIN_ACCOUNT_WORD = 'Main account';
export const MAIN_UNKNOWN_WORD = 'main account: we do not know';
export const LINK_ADD_ROLES = Object.freeze(['contact', 'pilot', 'named_insured', 'member', 'signer', 'payer']);
export const LINK_DOOR_WORDS = Object.freeze({
  add: 'Add person', add_role: 'Add role', end: 'End', make_main: 'Make main account',
  add_title: 'Add a person to this account', add_role_title: 'Add a role on this account',
  end_title: 'End this link', make_main_title: 'Make this the main account',
  end_note: 'The link is ended, never deleted; the reason is kept with it.',
  make_main_note: "Zoho's Contact is updated to name this account (its Account_Name is a copy of the main account). Links to other accounts stay.",
  search_placeholder: 'Search people by name or phone', role: 'Role', reason: 'Reason', pick_role: 'Pick a role',
  confirm_add: 'Add link', confirm_end: 'End link', confirm_main: 'Make main account', cancel: 'Cancel', saving: 'Saving',
  search_failed: 'Could not search people. Try again.', search_empty: 'No one found.', searching: 'Searching',
});
export const LINKS_UNREADABLE = 'TRACON could not read whether its account people are the record, so Add, End and Make main account are not offered on this read.';
export const STANDING_WORDS = Object.freeze({
  on_policy: 'On the policy', opw: 'Covered by the OPW', seeking_approval: 'Seeking approval',
  excluded: 'Excluded', undetermined: 'Undetermined',
});
export const AR_WORDS = Object.freeze({
  succeeded: 'Paid', pending: 'Unpaid', waived: 'Waived', deferred: 'Deferred', failed: 'Payment failed',
  refunded: 'Refunded', collected_elsewhere: 'Collected elsewhere',
});
export const AP_WORDS = Object.freeze({ pending: 'Owed to the carrier', remitted: 'Remitted', waived: 'Waived', offset: 'Offset' });
export const PERIOD_SOURCE_WORDS = Object.freeze({
  ledger: null, ambiguous_ledger: 'ambiguous ledger', legacy_unverified: 'legacy, unverified', none: 'no period recorded',
});
export const ENTITY_SOURCE_WORDS = Object.freeze({ stated: 'stated', derived: 'derived from the name' });
export const IDENTITY_SOURCE_WORDS = Object.freeze({ faa: 'from the FAA registry, unconfirmed', confirmed: 'identity confirmed', amin: 'AMIN' });
export const PHF_WORDS = Object.freeze({ invited: 'PHF invited', in_progress: 'PHF in progress', submitted: 'PHF submitted', verified: 'PHF verified', removed: 'PHF removed' });
export const CLOSED_WORDS = Object.freeze({
  lost: 'Lost', cancelled: 'Cancelled', expired: 'Expired', placed_elsewhere: 'Placed elsewhere', not_renewed: 'Not renewed',
  // The legacy cancellation marker (main-fixes 2026-10-29): utils/cancellation.js LEGACY_CANCELLED_LABEL, the same words.
  legacy_cancellation: 'Cancelled (legacy, no documents on file)',
});
// A stated reason a term ended (main-fixes 2026-10-29: aircraft sold is a reason, never an outcome; utils/closed-reason.js), with the
// words for the move that states it at the end of a term (POST /update-stage ended_because).
export const ENDED_BECAUSE_WORDS = Object.freeze({ aircraft_sold: 'Not renewed: aircraft sold' });
export const AIRCRAFT_SOLD_WORDS = ': aircraft sold';
export const INTEREST_STATUS_WORDS = Object.freeze({ active: 'active', ended: 'ended' });
export const ON_BINDER_WORDS = Object.freeze({ true: 'on the binder', false: 'not on the binder', null: 'binder not recorded' });
export const CERT_WORDS = Object.freeze({ none: 'no certificate', issued: 'certificate issued', requested: 'certificate requested' });

export function word(map, value) {
  if (value == null || value === '') return NOT_RECORDED;
  return Object.prototype.hasOwnProperty.call(map, value) ? map[value] : String(value);
}

// The key a fact row and an AIRgus item meet on: subject:id:field.
export function refKey(ref) {
  return ref && ref.subject && ref.field ? `${ref.subject}:${ref.id}:${ref.field}` : null;
}

export function indexItems(items) {
  const byRef = new Map();
  const loose = [];
  for (const it of items || []) {
    const k = refKey(it.fact_ref);
    if (!k) { loose.push(it); continue; }
    if (!byRef.has(k)) byRef.set(k, []);
    byRef.get(k).push(it);
  }
  return { byRef, loose };
}

// THE SCENARIO BUILDER'S WORDS (stage C4). A scenario's pilot cell is the
// membership scenario_pilots carries (rated | excluded) or, with no row for
// that pilot on that aircraft, NOT_ON. A market cell is submission_markets
// .state as stored, or NOT_SENT when the scenario has no row for that
// carrier. Nothing here decides a membership or a state.
export const MEMBERSHIP_WORDS = Object.freeze({ rated: 'Named', excluded: 'Excluded' });
export const NOT_ON = 'Not on';
export const MARKET_STATE_WORDS = Object.freeze({
  sent: 'Sent', acknowledged: 'Acknowledged', working: 'Working', quoted: 'Quoted', declined: 'Declined',
  blocked: 'Blocked', no_response: 'No response',
});
export const NOT_SENT = 'Not sent';
// The three words the reader answers (is_bound, is_selected, else open):
// submission_scenarios carries no status column, so no fourth word exists.
export const SCENARIO_STATUS_WORDS = Object.freeze({ open: 'Open', selected: 'Selected', bound: 'Bound' });
// A term's market rows the reader could not place under a scenario render
// once under this word, never under every scenario (contract rule 6).
export const UNATTRIBUTED = 'Not attributed to a scenario';
// WHY a row is unattributed, the reader's three reasons (contract rule 6): a
// mismatch is a data defect and must not read like the other two.
export const UNATTRIBUTED_REASON_WORDS = Object.freeze({
  mismatch: 'names a scenario this term does not hold',
  ambiguous: 'the term carries more than one scenario',
  no_scenario: 'the term carries no scenario',
});

// THE COVERAGE BLOCK'S WORDS (stage D1, contract rule 10). A null limit or
// deductible is NOT_STATED (the contract's own word for a null business
// answer: nobody stated it, which is not the same as nothing recorded about
// the term). The source word says which store spoke; a null source renders
// no word beside the block, and the rows all read NOT_STATED.
export const NOT_STATED = 'not stated';
export const COVERAGE_SOURCE_WORDS = Object.freeze({ bound_quote: 'from the bound quote', legacy_deal: 'from the CRM Deal, legacy' });
// liability.smooth is tri-state: only true earns the word; false renders the
// per-passenger sublimit; null claims neither.
export const SMOOTH_WORD = 'smooth';
export const NOT_IN_MOTION = 'not in motion';
export const IN_MOTION = 'in motion';

// THE POLICY NUMBER DOOR'S WORDS (stage D2). The input renders the server's
// answer inline: the re-read value with SAVED_REREADING while the account
// re-fetch is in flight, or the error code through this map (the code itself
// when no word is mapped), never a toast alone.
export const POLICY_NUMBER_ERROR_WORDS = Object.freeze({
  stated_value_protected: 'a higher-ranked source states this number; a typed one does not replace it',
  broker_jwt_required: 'sign in as a broker to save',
  v3_api_disabled: 'the v3 API is off; nothing was saved',
  not_found: 'this term is not in the v3 policies table',
  bad_request: 'the number was refused',
  store_unavailable: 'the store did not answer; nothing was saved',
});
export const SAVED_REREADING = 'saved, re-reading the account';

// THE DROP ZONE'S WORDS (stage D3, Track B's docs/v3-documents-api.md). The
// class chips are the writer's GUESSABLE set (every class but unclassified,
// which means "not yet decided" and is never offered as a decision); `other`
// is the sketch's "Something else". The compared words are the server's own
// four answers; the page compares nothing itself. An upload or confirm error
// is the server's code rendered in place through DOCUMENT_ERROR_WORDS, never
// a toast and never the console.
// paperwork-1 L1b-B3: the words learn every class of DOCUMENT_CLASSES (services/money/documents.service.js), the six of the
// document home (certificate, cancellation, NDEMNIFLY binder, not yet named; policy and endorsement were here) and the
// signed application (tests/document-class-words.test.js holds the two lists equal).
export const DOCUMENT_CLASS_WORDS = Object.freeze({
  quote: 'Quote', policy: 'Policy', binder: 'Binder', endorsement: 'Endorsement', other: 'Something else',
  statement: 'Statement', invoice: 'Invoice', credit_memo: 'Credit memo', receipt: 'Receipt', bank_export: 'Bank export',
  certificate: 'Certificate', cancellation: 'Cancellation confirmation', application: 'Signed application', bundle: 'NDEMNIFLY binder', unclassified: 'Not yet named',
});
// The sketch's five first, the carrier's other papers, then ours, the money classes, and the not-yet-named import last.
export const DOCUMENT_CLASS_ORDER = Object.freeze(['quote', 'policy', 'binder', 'endorsement', 'certificate', 'cancellation', 'other', 'application', 'bundle', 'statement', 'invoice', 'credit_memo', 'receipt', 'bank_export', 'unclassified']);
export const COMPARED_WORDS = Object.freeze({ new: 'New', matches: 'Matches', differs: 'Differs', no_term: 'no term to compare' });
export const GUESS_FIELD_LABELS = Object.freeze({
  policy_number: 'Policy number', effective_date: 'Effective', expiration_date: 'Expires', premium: 'Premium',
  named_insured: 'Named insured', carrier: 'Carrier', interests_present: 'Interests named',
});
export const DOCUMENT_ERROR_WORDS = Object.freeze({
  too_large: 'over 25 MB; not stored',
  unsupported_media_type: 'not a PDF, image, Word, Excel, CSV, text, .eml or .msg file; not stored',
  executable_refused: 'an executable, a script or a macro file; TRACON does not keep it',
  no_extension: 'the file has no extension; add one (.pdf, .csv, ...) and drop it again',
  bad_request: 'the upload was refused',
  not_found: 'this account is not in the v3 accounts table',
  policy_not_on_account: 'that term is not on this account',
  already_confirmed: 'already filed; nothing changed',
  on_binder_needs_binder: 'interests go on a binder only',
  reason_required: 'a reason is required to file it again',
  stated_value_protected: 'a higher-ranked source states the policy number; the document did not replace it',
  broker_jwt_required: 'sign in as a broker to file it',
  v3_api_disabled: 'the v3 API is off; nothing was stored',
  store_unavailable: 'the store did not answer; nothing was stored',
  timeout: 'the upload timed out; not stored',
  internal: 'the server failed; not stored',
});
export const UNSURE_WORD = "Couldn't tell what this is";
export const STORED_NOT_FILED = 'stored, not filed';
export const ALREADY_ON_FILE = 'already on file';
export const FILED_REREADING = 'filed, re-reading the account';
export const ACCOUNT_ONLY = 'Account only, no policy';
export const NOTHING_STORED_FIXTURE = 'fixture guess; nothing was stored';
// A premium the broker typed that is not a number is not sent at all (the
// key is omitted from the confirm body); the row says so in place.
export const PREMIUM_NOT_NUMBER = 'not a number; the premium is not sent';
export const CLEARED_BASIS_WORDS = Object.freeze({ airgus: null, airgus_hook_absent: 'AIRgus is not on this mount, so what cleared was not checked' });

// THE CONVERSATION'S WORDS (stage D3, Track A's docs/v3-mail-api.md). The
// badge is messages.status as stored, exactly the contract's eight; opened
// and clicked are SIGNALS (message_events and the view), rendered lighter
// beside the badge and never as it. likely_automatic is the view's answer:
// only true earns the word, false says nothing more, null says nothing (no
// delivered event to measure against is not "human").
export const MESSAGE_STATUS_WORDS = Object.freeze({
  queued: 'Queued', sent: 'Sent', delivered: 'Delivered', bounced: 'Bounced', complained: 'Complained',
  replied: 'Replied', failed: 'Failed', received: 'Received',
});
export const MESSAGE_STATUS_CLASS = Object.freeze({
  queued: 'bg-border/60 text-text-muted', sent: 'bg-border/60 text-text', delivered: 'bg-info-soft text-info',
  bounced: 'bg-danger-soft text-danger', complained: 'bg-danger-soft text-danger', replied: 'bg-success-soft text-success',
  failed: 'bg-danger-soft text-danger', received: 'bg-accent-soft text-accent',
});
export const DIRECTION_WORDS = Object.freeze({ outbound: 'out', inbound: 'in' });
export const CHANNEL_WORDS = Object.freeze({ email: 'email', sms: 'SMS' });
export const LIKELY_AUTOMATIC = 'likely automatic';
export const MATCH_WORDS = Object.freeze({ matched: 'matched', unmatched: 'unmatched' });
export const MATCHED_BY_WORDS = Object.freeze({ reply_token: 'by reply token', sender_email: 'by sender address', manual: 'by hand' });
export const NOT_SENT_YET = 'not sent yet';
export const NOT_READ_YET = 'not read yet';
// Until v3-mail and v3-docs merge, the server answers their routes with its
// unbuilt-route 404 (services/v3.service.js NOT_CONNECTED): a quiet line, never an error.
export const MAIL_NOT_CONNECTED = 'Messages arrive when mail is connected';
export const DOCS_NOT_CONNECTED = 'Documents arrive when docs is connected';
export const NO_MESSAGES = 'No messages';
export const UNATTRIBUTED_MESSAGES = 'Not attributed to a term';
export const EARLIER_NOT_LOADED = 'earlier messages not loaded';
export const CONVERSATION_ERROR_WORDS = Object.freeze({
  v3_api_disabled: 'the v3 API is off; the conversation was not read',
  store_unavailable: 'the store did not answer; the conversation was not read',
  not_found: 'this account is not in the v3 accounts table',
  message_not_found: 'this message is not in the store',
  broker_jwt_required: 'sign in as a broker to read the conversation',
});

// THE RENEWAL LEAD (RenewalLead.jsx) and the fold of empty term rows (TermBlock.jsx FoldBox).
export const PILOT_ACTION_WORDS = Object.freeze({ attested: 'Nothing to do', invited: 'Waiting on the pilot to open the form', in_progress: 'Waiting on the pilot to finish the form', not_attested: 'Form is in, needs your attestation' });
export const LEAD_NO_QUOTES = 'no quotes yet';
export const LEAD_NO_AIRCRAFT = 'no aircraft on the account';
export const FOLD_TAIL = 'not recorded yet';
export const FALLBACK_FROM = 'from the policy period';
export const SUCCESSOR_WORDS = Object.freeze({ link: 'renewed by the linked term', rewrite: 'followed by its linked rewrite or re-up', pending: 'renewal in progress', inferred: 'looks renewed, not linked' });
// The rewrite link door's refusals (POST /api/v3/policies/:id/rewrite-of), in words beside the button.
export const REWRITE_LINK_ERROR_WORDS = Object.freeze({ rewrite_exists: 'this term already has a rewrite', rewrite_of_other: 'that term is already the rewrite of another', rewrite_cycle: 'the two terms would follow each other', already_follows: 'that term already renews another term', source_already_renewed: 'this term is already linked to its renewal', no_deal_id: 'one of the terms has no deal', store_unavailable: 'could not check; nothing was linked', not_found: 'a term was not found' });
